/**
 * Olive Oil Quality Checker
 * Scoring algorithm and form logic
 */

(function() {
  'use strict';

  // Brand database cache
  let brandsData = null;
  let selectedBrand = null;
  let selectedProduct = null;

  // High-polyphenol olive varieties
  const HIGH_POLYPHENOL_VARIETIES = ['Koroneiki', 'Picual', 'Coratina', 'Moraiolo', 'Ogliarola', 'Olympia', 'Lianolia'];

  // DOM Elements
  const form = document.getElementById('quality-checker-form');
  const brandInput = document.getElementById('qc-brand');
  const brandIdInput = document.getElementById('qc-brand-id');
  const suggestionsContainer = document.getElementById('qc-brand-suggestions');
  const productField = document.getElementById('qc-product-field');
  const productSelect = document.getElementById('qc-product');
  const harvestSelect = document.getElementById('qc-harvest');
  const bestByField = document.getElementById('qc-bestby-field');
  const timeOpenField = document.getElementById('qc-timeopen-field');
  const resultsContainer = document.getElementById('quality-checker-results');
  const resetButton = document.getElementById('qc-reset');

  // Rankings elements
  const rankingsLoading = document.getElementById('qc-rankings-loading');
  const rankingsTableWrapper = document.getElementById('qc-rankings-table-wrapper');
  const rankingsTbody = document.getElementById('qc-rankings-tbody');
  const rankingsActions = document.getElementById('qc-rankings-actions');
  const showAllButton = document.getElementById('qc-show-all');
  const remainingCount = document.getElementById('qc-remaining-count');

  /**
   * Initialize the quality checker
   */
  async function init() {
    try {
      await loadBrandsData();
      setupEventListeners();
      renderRankingsTable();
    } catch (error) {
      console.error('Failed to initialize quality checker:', error);
    }
  }

  /**
   * Load brand database from JSON
   */
  async function loadBrandsData() {
    // Get the asset URL from Liquid config (set in quality-checker-tool.liquid)
    const configUrl = window.QualityCheckerConfig?.brandsDataUrl;

    if (!configUrl) {
      console.error('QualityCheckerConfig.brandsDataUrl not set');
      throw new Error('Brand data URL not configured');
    }

    const response = await fetch(configUrl + '?v=' + Date.now());
    if (!response.ok) {
      throw new Error('Failed to load brands data: ' + response.status);
    }

    brandsData = await response.json();
    return brandsData;
  }

  /**
   * Setup event listeners
   */
  function setupEventListeners() {
    // Brand autocomplete
    if (brandInput) {
      brandInput.addEventListener('input', handleBrandInput);
      brandInput.addEventListener('focus', handleBrandInput);
      brandInput.addEventListener('blur', () => {
        setTimeout(() => hideSuggestions(), 200);
      });
    }

    // Product selection
    if (productSelect) {
      productSelect.addEventListener('change', handleProductSelect);
    }

    // Harvest date change (show/hide best-by field)
    if (harvestSelect) {
      harvestSelect.addEventListener('change', handleHarvestChange);
    }

    // Opened status change
    document.querySelectorAll('input[name="opened"]').forEach(radio => {
      radio.addEventListener('change', handleOpenedChange);
    });

    // Form submission
    if (form) {
      form.addEventListener('submit', handleFormSubmit);
    }

    // Reset button
    if (resetButton) {
      resetButton.addEventListener('click', handleReset);
    }

    // Show all button for rankings
    if (showAllButton) {
      showAllButton.addEventListener('click', handleShowAll);
    }
  }

  /**
   * Handle brand input for autocomplete
   */
  function handleBrandInput(e) {
    const query = e.target.value.toLowerCase().trim();

    if (query.length < 2) {
      hideSuggestions();
      return;
    }

    const matches = brandsData.brands.filter(brand =>
      brand.brand_name.toLowerCase().includes(query) ||
      brand.slug.toLowerCase().includes(query)
    ).slice(0, 8);

    if (matches.length > 0) {
      showSuggestions(matches, query);
    } else {
      showSuggestions([{ brand_id: 'other', brand_name: 'Other / Not Listed', slug: 'other' }], query);
    }
  }

  /**
   * Show autocomplete suggestions
   */
  function showSuggestions(matches, query) {
    let html = matches.map(brand => {
      const isOther = brand.brand_id === 'other';
      const highlightedName = isOther ? brand.brand_name :
        brand.brand_name.replace(new RegExp(`(${query})`, 'gi'), '<strong>$1</strong>');

      return `
        <div class="qc-tool__suggestion" data-brand-id="${brand.brand_id}">
          <span class="qc-tool__suggestion-name">${highlightedName}</span>
          ${!isOther && brand.country_of_origin ? `<span class="qc-tool__suggestion-country">${brand.country_of_origin}</span>` : ''}
        </div>
      `;
    }).join('');

    // Add "Other" option if not already in list
    if (!matches.find(m => m.brand_id === 'other')) {
      html += `
        <div class="qc-tool__suggestion qc-tool__suggestion--other" data-brand-id="other">
          <span class="qc-tool__suggestion-name">Other / Not Listed</span>
        </div>
      `;
    }

    suggestionsContainer.innerHTML = html;
    suggestionsContainer.style.display = 'block';

    // Add click listeners to suggestions
    suggestionsContainer.querySelectorAll('.qc-tool__suggestion').forEach(el => {
      el.addEventListener('click', () => selectBrand(el.dataset.brandId));
    });
  }

  /**
   * Hide autocomplete suggestions
   */
  function hideSuggestions() {
    if (suggestionsContainer) {
      suggestionsContainer.style.display = 'none';
    }
  }

  /**
   * Select a brand from autocomplete
   */
  function selectBrand(brandId) {
    hideSuggestions();

    if (brandId === 'other') {
      selectedBrand = null;
      brandInput.value = brandInput.value || 'Other';
      brandIdInput.value = 'other';
      productField.style.display = 'none';
      return;
    }

    selectedBrand = brandsData.brands.find(b => b.brand_id === brandId);
    if (!selectedBrand) return;

    brandInput.value = selectedBrand.brand_name;
    brandIdInput.value = brandId;

    // Populate products if available
    if (selectedBrand.products && selectedBrand.products.length > 0) {
      populateProducts(selectedBrand.products);
      productField.style.display = 'block';
    } else {
      productField.style.display = 'none';
    }

    // Pre-fill container type if known
    if (selectedBrand.products && selectedBrand.products.length > 0) {
      const firstProduct = selectedBrand.products[0];
      if (firstProduct.container_type) {
        const radio = document.querySelector(`input[name="container_type"][value="${firstProduct.container_type}"]`);
        if (radio) radio.checked = true;
      }
    }
  }

  /**
   * Populate product dropdown
   */
  function populateProducts(products) {
    productSelect.innerHTML = '<option value="">Select a product...</option>';
    products.forEach((product, index) => {
      const option = document.createElement('option');
      option.value = index;
      option.textContent = product.product_name;
      productSelect.appendChild(option);
    });
  }

  /**
   * Handle product selection
   */
  function handleProductSelect(e) {
    const index = parseInt(e.target.value);
    if (isNaN(index) || !selectedBrand) {
      selectedProduct = null;
      return;
    }
    selectedProduct = selectedBrand.products[index];

    // Pre-fill container type
    if (selectedProduct.container_type) {
      const radio = document.querySelector(`input[name="container_type"][value="${selectedProduct.container_type}"]`);
      if (radio) radio.checked = true;
    }
  }

  /**
   * Handle harvest date change
   */
  function handleHarvestChange(e) {
    const value = e.target.value;
    if (value === 'not-listed' || value === 'unknown') {
      bestByField.style.display = 'block';
    } else {
      bestByField.style.display = 'none';
    }
  }

  /**
   * Handle opened status change
   */
  function handleOpenedChange(e) {
    if (e.target.value === 'yes') {
      timeOpenField.style.display = 'block';
    } else {
      timeOpenField.style.display = 'none';
    }
  }

  /**
   * Handle form submission
   */
  function handleFormSubmit(e) {
    e.preventDefault();

    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());

    // Calculate score
    const scoreResult = calculateScore(data);

    // Display results
    displayResults(scoreResult, data);
  }

  /**
   * Calculate health score
   */
  function calculateScore(data) {
    let polyphenolScore = 0;
    let transparencyScore = 0;
    let freshnessScore = 0;
    let packagingScore = 0;
    let bonusScore = 0;
    const issues = [];

    // Get brand/product data
    const brand = selectedBrand;
    const product = selectedProduct;

    // 1. POLYPHENOL SCORE (40 pts max)
    let polyphenolContent = product?.polyphenol_content || brand?.products?.[0]?.polyphenol_content || null;
    let polyphenolSource = product?.polyphenol_source || brand?.products?.[0]?.polyphenol_source || 'unknown';

    if (polyphenolContent === null) {
      polyphenolContent = 250; // default assumption
      polyphenolSource = 'unknown';
    }

    if (polyphenolContent >= 1000) {
      polyphenolScore = 40;
    } else if (polyphenolContent >= 750) {
      polyphenolScore = 35;
    } else if (polyphenolContent >= 500) {
      polyphenolScore = 30;
    } else if (polyphenolContent >= 250) {
      polyphenolScore = 20;
    } else if (polyphenolContent >= 100) {
      polyphenolScore = 10;
      issues.push('Low polyphenol content (under 250 mg/kg) means minimal health benefits');
    } else {
      polyphenolScore = 0;
      issues.push('Very low polyphenol content — unlikely to provide health benefits');
    }

    // Apply source weighting
    if (polyphenolSource === 'claimed') {
      polyphenolScore = Math.round(polyphenolScore * 0.8);
    } else if (polyphenolSource === 'unknown') {
      polyphenolScore = 12; // default score for unknown
      issues.push('No published polyphenol data — health benefits unverified');
    }

    // 2. TRANSPARENCY SCORE (25 pts max)
    if (brand) {
      if (product?.lab_results_available || brand.third_party_tested) {
        transparencyScore += 8;
      } else {
        issues.push('No published lab results means health claims are unverified');
      }

      if (brand.harvest_date_displayed || product?.harvest_date_on_bottle) {
        transparencyScore += 6;
      } else {
        issues.push('No harvest date usually means old, potentially degraded oil');
      }

      if (brand.third_party_tested) {
        transparencyScore += 5;
      }

      if (brand.single_origin) {
        transparencyScore += 3;
      } else if (brand.single_origin === false) {
        issues.push('Multi-country blends make quality and freshness impossible to verify');
      }

      if (product?.lot_number_on_bottle) {
        transparencyScore += 3;
      }
    } else {
      // Unknown brand - benefit of the doubt
      transparencyScore = 5;
      issues.push('Unknown brand — we recommend researching their transparency practices');
    }

    // 3. PACKAGING SCORE (15 pts max, can go negative)
    const containerType = data.container_type || product?.container_type || 'unknown';

    switch (containerType) {
      case 'dark_glass':
      case 'tin':
        packagingScore = 15;
        break;
      case 'clear_glass':
        packagingScore = 5;
        issues.push('Clear glass allows light degradation of polyphenols');
        break;
      case 'plastic':
        packagingScore = -10;
        issues.push('Plastic packaging actively degrades polyphenols and may leach chemicals');
        break;
      default:
        packagingScore = 5;
    }

    // 4. FRESHNESS SCORE (20 pts max, can go negative)
    const harvestDate = data.harvest_date;
    let monthsFromHarvest = null;

    if (harvestDate && harvestDate !== 'not-listed' && harvestDate !== 'unknown') {
      const harvestYearMap = {
        'fall-2025': 4,   // ~4 months ago
        'fall-2024': 16,  // ~16 months ago
        'fall-2023': 28,  // ~28 months ago
        'before-2023': 40 // 40+ months
      };
      monthsFromHarvest = harvestYearMap[harvestDate] || 24;
    } else if (data.best_by_date) {
      // Estimate harvest from best-by (typically 2 years after harvest)
      const bestBy = new Date(data.best_by_date);
      const estimatedHarvest = new Date(bestBy.setFullYear(bestBy.getFullYear() - 2));
      monthsFromHarvest = Math.floor((new Date() - estimatedHarvest) / (1000 * 60 * 60 * 24 * 30));
    } else {
      // Unknown freshness
      monthsFromHarvest = 18; // assume average
      issues.push('Unknown harvest date — freshness cannot be verified');
    }

    if (monthsFromHarvest < 6) {
      freshnessScore = 20;
    } else if (monthsFromHarvest < 12) {
      freshnessScore = 15;
    } else if (monthsFromHarvest < 18) {
      freshnessScore = 10;
    } else if (monthsFromHarvest < 24) {
      freshnessScore = 5;
      issues.push('Oil is 18-24 months old — polyphenols significantly degraded');
    } else if (monthsFromHarvest < 36) {
      freshnessScore = 0;
      issues.push('Oil is over 2 years old — likely lost most health benefits');
    } else {
      freshnessScore = -10;
      issues.push('Oil is over 3 years old — may be rancid');
    }

    // Opened modifier
    if (data.opened === 'yes' && data.time_open) {
      switch (data.time_open) {
        case 'less-1-month':
          // No penalty
          break;
        case '1-3-months':
          freshnessScore -= 3;
          break;
        case '3-6-months':
          freshnessScore -= 8;
          issues.push('Oil has been open 3-6 months — accelerated oxidation');
          break;
        case 'more-6-months':
          freshnessScore -= 15;
          issues.push('Oil has been open over 6 months — likely significantly degraded');
          break;
      }
    }

    // 5. BONUS POINTS
    if (brand) {
      const certs = brand.certifications || [];
      const certsLower = certs.map(c => c.toLowerCase());

      if (certsLower.some(c => c.includes('organic'))) {
        bonusScore += 3;
      }

      if (certsLower.some(c => c.includes('pdo') || c.includes('pgi') || c.includes('dop'))) {
        bonusScore += 3;
      }

      if (certsLower.some(c => c.includes('cooc'))) {
        bonusScore += 2;
      }

      // Awards
      const awards = product?.awards || [];
      if (awards.length > 0) {
        bonusScore += 2;
      }

      // Acidity bonus
      const acidity = product?.acidity;
      if (acidity !== null && acidity !== undefined) {
        if (acidity <= 0.3) {
          bonusScore += 3;
        } else if (acidity <= 0.5) {
          bonusScore += 1;
        } else if (acidity > 0.8) {
          bonusScore -= 3;
        }
      }

      // High-polyphenol variety bonus
      const varieties = product?.olive_variety || [];
      if (varieties.some(v => HIGH_POLYPHENOL_VARIETIES.includes(v))) {
        bonusScore += 3;
      }
    }

    // Calculate total (cap at 0 minimum, 100 max)
    let totalScore = polyphenolScore + transparencyScore + freshnessScore + packagingScore + bonusScore;
    totalScore = Math.max(0, Math.min(100, totalScore));

    return {
      total: totalScore,
      polyphenol: polyphenolScore,
      transparency: transparencyScore,
      freshness: freshnessScore,
      packaging: packagingScore,
      bonus: bonusScore,
      issues: issues,
      polyphenolContent: polyphenolContent,
      polyphenolSource: polyphenolSource
    };
  }

  /**
   * Display results
   */
  function displayResults(score, data) {
    // Hide form, show results
    document.querySelector('.qc-tool__widget').style.display = 'none';
    resultsContainer.style.display = 'block';

    // Animate score
    animateScore(score.total);

    // Set rating text and color
    const ratingEl = document.getElementById('qc-rating');
    const { rating, ratingClass, primaryMessage, secondaryMessage } = getScoreTier(score.total, score.issues);

    ratingEl.textContent = rating;
    ratingEl.className = 'qc-results__rating ' + ratingClass;

    // Set messages
    document.getElementById('qc-primary-message').textContent = primaryMessage;
    document.getElementById('qc-secondary-message').textContent = secondaryMessage;

    // Update breakdown bars
    updateBreakdownBar('polyphenols', score.polyphenol, 40);
    updateBreakdownBar('transparency', score.transparency, 25);
    updateBreakdownBar('freshness', Math.max(0, score.freshness), 20);
    updateBreakdownBar('packaging', Math.max(0, score.packaging), 15);

    // Show bonus if applicable
    if (score.bonus > 0) {
      document.getElementById('qc-bonus-row').style.display = 'flex';
      updateBreakdownBar('bonus', score.bonus, 15);
      document.getElementById('qc-val-bonus').textContent = '+' + score.bonus;
    }

    // Show issues for lower scores
    if (score.issues.length > 0 && score.total < 85) {
      const issuesContainer = document.getElementById('qc-issues');
      const issuesList = document.getElementById('qc-issues-list');

      issuesList.innerHTML = score.issues.map(issue => `<li>${issue}</li>`).join('');
      issuesContainer.style.display = 'block';

      // Update title based on score
      const issuesTitle = issuesContainer.querySelector('.qc-results__issues-title');
      if (score.total < 50) {
        issuesTitle.textContent = '⚠️ Red Flags';
      } else {
        issuesTitle.textContent = 'Areas for Improvement';
      }
    }

    // Render CTA card based on score tier
    renderCtaCard(score, data);

    // Scroll to results
    resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Animate score counter
   */
  function animateScore(targetScore) {
    const scoreEl = document.getElementById('qc-score-number');
    const gaugeFill = document.querySelector('.qc-results__gauge-fill');
    let current = 0;
    const duration = 1500;
    const startTime = performance.now();

    // Calculate gauge stroke offset
    const circumference = 251.2; // Approximate for the arc
    const maxOffset = circumference * 0.5; // 50% of arc = full score

    function update(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      current = Math.round(easeProgress * targetScore);
      scoreEl.textContent = current;

      // Update gauge
      const offset = maxOffset - (easeProgress * targetScore / 100) * maxOffset;
      gaugeFill.style.strokeDashoffset = offset;

      // Update gauge color based on score
      const color = getScoreColor(current);
      gaugeFill.style.stroke = color;

      if (progress < 1) {
        requestAnimationFrame(update);
      }
    }

    requestAnimationFrame(update);
  }

  /**
   * Get score color
   */
  function getScoreColor(score) {
    if (score >= 85) return '#2d6a4f';
    if (score >= 70) return '#7cb518';
    if (score >= 50) return '#e9c46a';
    if (score >= 30) return '#f4a261';
    return '#e76f51';
  }

  /**
   * Get score tier info
   */
  function getScoreTier(score, issues) {
    if (score >= 85) {
      return {
        rating: 'Excellent',
        ratingClass: 'qc-results__rating--excellent',
        primaryMessage: `Your olive oil scores ${score} — Excellent.`,
        secondaryMessage: "You clearly know what to look for. Your oil has the transparency, packaging, and freshness markers that indicate real health benefits. Keep doing what you're doing."
      };
    } else if (score >= 70) {
      const weakness = issues[0] || 'room for improvement in transparency';
      return {
        rating: 'Good',
        ratingClass: 'qc-results__rating--good',
        primaryMessage: `Your olive oil scores ${score} — Good, but not great.`,
        secondaryMessage: `Your oil is above average, but there's room for improvement. You might be missing out on some health benefits due to ${weakness.toLowerCase()}.`
      };
    } else if (score >= 50) {
      return {
        rating: 'Average',
        ratingClass: 'qc-results__rating--average',
        primaryMessage: `Your olive oil scores ${score} — Average.`,
        secondaryMessage: "Your oil is... fine. It's probably safe to eat, but it's likely not delivering the cardiovascular and anti-inflammatory benefits that high-quality olive oil provides. The good news: small upgrades make a big difference."
      };
    } else if (score >= 30) {
      return {
        rating: 'Below Average',
        ratingClass: 'qc-results__rating--below',
        primaryMessage: `Your olive oil scores ${score} — Below Average.`,
        secondaryMessage: "Your oil has some red flags. It may still be safe to consume, but it's probably not delivering the health benefits you expect from olive oil."
      };
    } else {
      return {
        rating: 'Poor',
        ratingClass: 'qc-results__rating--poor',
        primaryMessage: `Your olive oil scores ${score} — Poor.`,
        secondaryMessage: "We hate to be the bearers of bad news, but your oil has multiple issues that significantly reduce its health value. In some cases, oils in this range may be rancid or adulterated."
      };
    }
  }

  /**
   * Update breakdown bar
   */
  function updateBreakdownBar(category, score, max) {
    const bar = document.getElementById(`qc-bar-${category}`);
    const value = document.getElementById(`qc-val-${category}`);

    if (bar && value) {
      const percentage = Math.max(0, (score / max) * 100);
      bar.style.width = percentage + '%';
      bar.style.backgroundColor = getScoreColor((score / max) * 100);
      value.textContent = score;
    }
  }

  /**
   * Render CTA card based on score tier
   */
  function renderCtaCard(score, data) {
    const ctaCard = document.getElementById('qc-cta-card');
    let html = '';

    if (score.total >= 85) {
      html = `
        <div class="qc-results__cta-header">Similar Quality, Great Value</div>
        <p>If you're ever looking for a refill or backup bottle, Olivea delivers the same quality tier at direct-to-consumer pricing.</p>
        <ul class="qc-results__cta-list">
          <li>✓ 600+ mg/kg polyphenols (every batch lab-tested)</li>
          <li>✓ Formulated with Harvard-trained cardiologists</li>
          <li>✓ Ships fresh from this season's harvest</li>
          <li>✓ Published lab results for every lot</li>
        </ul>
        <a href="/collections/olive-oil" class="qc-results__cta-button">Shop Olivea →</a>
      `;
    } else if (score.total >= 70) {
      const userPolyphenols = score.polyphenolContent || '~250';
      const labVerified = score.transparency >= 8 ? 'Yes' : 'Unknown';
      html = `
        <div class="qc-results__cta-header">How Your Oil Compares to Olivea</div>
        <div class="qc-results__comparison">
          <div class="qc-results__comparison-row qc-results__comparison-row--header">
            <span></span>
            <span>Your Oil</span>
            <span>Olivea Everyday</span>
          </div>
          <div class="qc-results__comparison-row">
            <span>Polyphenols</span>
            <span>${userPolyphenols} mg/kg*</span>
            <span class="qc-results__comparison-check">600 mg/kg ✓</span>
          </div>
          <div class="qc-results__comparison-row">
            <span>Lab Verified</span>
            <span>${labVerified}</span>
            <span class="qc-results__comparison-check">Yes ✓</span>
          </div>
          <div class="qc-results__comparison-row">
            <span>Harvest Date</span>
            <span>${data.harvest_date === 'not-listed' ? 'Missing' : 'Present'}</span>
            <span class="qc-results__comparison-check">On every bottle ✓</span>
          </div>
          <div class="qc-results__comparison-row">
            <span>Packaging</span>
            <span>${data.container_type?.replace('_', ' ') || 'Unknown'}</span>
            <span class="qc-results__comparison-check">Dark glass ✓</span>
          </div>
        </div>
        <p class="qc-results__cta-footnote">*Estimated based on available brand data</p>
        <div class="qc-results__cta-buttons">
          <a href="/blogs/news/olivea-lab-results" class="qc-results__cta-link">See Our Lab Results</a>
          <a href="/collections/olive-oil" class="qc-results__cta-button">Shop Now →</a>
        </div>
      `;
    } else if (score.total >= 50) {
      html = `
        <div class="qc-results__cta-header">What "Healthy" Olive Oil Actually Looks Like</div>
        <p>Most grocery store olive oils score in this range. They meet minimum standards but don't deliver meaningful polyphenol content.</p>
        <p><strong>Here's what to look for in your next bottle:</strong></p>
        <ul class="qc-results__cta-list">
          <li>✓ 500+ mg/kg polyphenols (not just "contains polyphenols")</li>
          <li>✓ Harvest date within the last 18 months</li>
          <li>✓ Dark glass or tin packaging</li>
          <li>✓ Published, third-party lab results</li>
        </ul>
        <p>Olivea checks every box.</p>
        <a href="/collections/olive-oil" class="qc-results__cta-button">See Why →</a>
      `;
    } else if (score.total >= 30) {
      html = `
        <div class="qc-results__cta-header">It Might Be Time for an Upgrade</div>
        <p>We built Olivea specifically for people who want olive oil that actually delivers on health claims — not just marketing.</p>
        <p><strong>Every bottle includes:</strong></p>
        <ul class="qc-results__cta-list">
          <li>✓ Batch-specific lab results you can verify</li>
          <li>✓ This season's harvest (never old stock)</li>
          <li>✓ Single-origin Greek olives (Koroneiki variety)</li>
          <li>✓ Dark glass packaging to protect polyphenols</li>
        </ul>
        <a href="/collections/olive-oil" class="qc-results__cta-button">Shop Olivea →</a>
      `;
    } else {
      html = `
        <div class="qc-results__cta-warning">
          <div class="qc-results__cta-warning-header">⚠️ Signs Your Oil May Be Rancid or Low-Quality</div>
          <ul>
            <li>Crayon-like, musty, or "off" smell</li>
            <li>Greasy, heavy mouthfeel without any pepperiness</li>
            <li>No throat catch or bitterness (indicates low polyphenols)</li>
            <li>Harvest date more than 2 years ago (or none listed)</li>
          </ul>
          <p>If your oil shows these signs, it's time for a new bottle.</p>
        </div>
        <div class="qc-results__cta-header">Start Fresh with Oil You Can Trust</div>
        <p>Olivea is formulated by cardiologists and verified by independent labs. No guessing, no old stock, no marketing fluff.</p>
        <div class="qc-results__cta-buttons">
          <a href="/collections/olive-oil" class="qc-results__cta-button">Shop Now →</a>
          <a href="/blogs/guides/does-olive-oil-go-bad" class="qc-results__cta-link">Take the Rancidity Test →</a>
        </div>
      `;
    }

    ctaCard.innerHTML = html;
  }

  /**
   * Handle reset
   */
  function handleReset() {
    // Reset form
    form.reset();
    selectedBrand = null;
    selectedProduct = null;
    brandIdInput.value = '';
    productField.style.display = 'none';
    bestByField.style.display = 'none';
    timeOpenField.style.display = 'none';

    // Hide results, show form
    resultsContainer.style.display = 'none';
    document.querySelector('.qc-tool__widget').style.display = 'block';

    // Reset breakdown
    document.getElementById('qc-bonus-row').style.display = 'none';
    document.getElementById('qc-issues').style.display = 'none';

    // Scroll to form
    document.getElementById('quality-checker-tool').scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // =====================
  // RANKINGS TABLE
  // =====================

  let allRankedBrands = [];
  let showingAll = false;
  const INITIAL_SHOW = 10;

  /**
   * Render rankings table
   */
  function renderRankingsTable() {
    if (!brandsData || !rankingsTbody) return;

    // Calculate health scores for all brands
    allRankedBrands = brandsData.brands
      .filter(brand => !brand.is_house_brand || brand.brand_id === 'olivea') // Include Olivea
      .map(brand => {
        const product = brand.products?.[0];
        const healthScore = calculateBrandHealthScore(brand, product);
        return { brand, product, healthScore };
      })
      .filter(item => item.healthScore >= 50 || item.brand.brand_id === 'olivea') // Filter low scores
      .sort((a, b) => b.healthScore - a.healthScore);

    // Hide loading, show table
    if (rankingsLoading) rankingsLoading.style.display = 'none';
    if (rankingsTableWrapper) rankingsTableWrapper.style.display = 'block';

    // Render initial rows
    renderRankingRows(INITIAL_SHOW);

    // Show "Show All" button if needed
    if (allRankedBrands.length > INITIAL_SHOW && rankingsActions) {
      rankingsActions.style.display = 'block';
      if (remainingCount) {
        remainingCount.textContent = `(${allRankedBrands.length - INITIAL_SHOW} more)`;
      }
    }
  }

  /**
   * Calculate health score for a brand (simplified for table)
   */
  function calculateBrandHealthScore(brand, product) {
    let score = 0;

    // Polyphenols (40 pts)
    const polyphenols = product?.polyphenol_content || 250;
    const source = product?.polyphenol_source || 'unknown';

    let polyScore = 0;
    if (polyphenols >= 1000) polyScore = 40;
    else if (polyphenols >= 750) polyScore = 35;
    else if (polyphenols >= 500) polyScore = 30;
    else if (polyphenols >= 250) polyScore = 20;
    else if (polyphenols >= 100) polyScore = 10;

    if (source === 'claimed') polyScore *= 0.8;
    else if (source === 'unknown') polyScore = 12;

    score += polyScore;

    // Transparency (25 pts)
    if (product?.lab_results_available || brand.third_party_tested) score += 8;
    if (brand.harvest_date_displayed || product?.harvest_date_on_bottle) score += 6;
    if (brand.third_party_tested) score += 5;
    if (brand.single_origin) score += 3;
    if (product?.lot_number_on_bottle) score += 3;

    // Packaging (15 pts)
    const container = product?.container_type || 'unknown';
    if (container === 'dark_glass' || container === 'tin') score += 15;
    else if (container === 'clear_glass') score += 5;
    else if (container === 'plastic') score -= 10;
    else score += 5;

    // Freshness (assume fresh for table, 15 pts)
    score += 15;

    // Bonus
    const certs = (brand.certifications || []).map(c => c.toLowerCase());
    if (certs.some(c => c.includes('organic'))) score += 3;
    if (certs.some(c => c.includes('pdo') || c.includes('dop'))) score += 3;
    if (certs.some(c => c.includes('cooc'))) score += 2;
    if (product?.awards?.length > 0) score += 2;

    return Math.max(0, Math.min(100, Math.round(score)));
  }

  /**
   * Render ranking rows
   */
  function renderRankingRows(count) {
    const items = showingAll ? allRankedBrands : allRankedBrands.slice(0, count);

    rankingsTbody.innerHTML = items.map((item, index) => {
      const { brand, product, healthScore } = item;
      const rank = index + 1;
      const isOlivea = brand.brand_id === 'olivea';
      const polyphenols = product?.polyphenol_content;
      const labVerified = product?.lab_results_available || brand.third_party_tested;
      const container = product?.container_type;

      // Determine link
      let brandLink = '#';
      if (isOlivea && product?.shopify_handle) {
        brandLink = `/products/${product.shopify_handle}`;
      } else if (brand.blog_mentions?.length > 0) {
        brandLink = brand.blog_mentions[0];
      }

      // Score badge class
      let badgeClass = 'qc-rankings__score-badge--average';
      if (healthScore >= 85) badgeClass = 'qc-rankings__score-badge--excellent';
      else if (healthScore >= 70) badgeClass = 'qc-rankings__score-badge--good';
      else if (healthScore < 50) badgeClass = 'qc-rankings__score-badge--below';

      return `
        <tr class="qc-rankings__tr ${isOlivea ? 'qc-rankings__tr--olivea' : ''}">
          <td class="qc-rankings__td qc-rankings__rank ${rank <= 3 ? 'qc-rankings__rank--top3' : ''}">${rank}</td>
          <td class="qc-rankings__td">
            <div class="qc-rankings__brand-cell">
              <a href="${brandLink}" class="qc-rankings__brand-name ${isOlivea ? 'qc-rankings__brand-name--olivea' : ''}">${brand.brand_name}</a>
              <span class="qc-rankings__product-name">${product?.product_name || ''}</span>
            </div>
          </td>
          <td class="qc-rankings__td qc-rankings__polyphenols ${polyphenols >= 500 ? 'qc-rankings__polyphenols--high' : ''} ${!polyphenols ? 'qc-rankings__polyphenols--unknown' : ''}">
            ${polyphenols ? polyphenols + ' mg/kg' : 'Unknown'}
          </td>
          <td class="qc-rankings__td qc-rankings__verified">
            ${labVerified ? '<span class="qc-rankings__check">✓</span>' : '<span class="qc-rankings__x">✗</span>'}
          </td>
          <td class="qc-rankings__td qc-rankings__packaging">
            ${formatContainer(container)}
          </td>
          <td class="qc-rankings__td" style="text-align: center;">
            <span class="qc-rankings__score-badge ${badgeClass}">${healthScore}</span>
          </td>
        </tr>
      `;
    }).join('');
  }

  /**
   * Format container type for display
   */
  function formatContainer(type) {
    const map = {
      'dark_glass': '🍾 Dark Glass',
      'clear_glass': '🍾 Clear Glass',
      'tin': '🥫 Tin',
      'plastic': '🧴 Plastic'
    };
    return map[type] || '—';
  }

  /**
   * Handle show all
   */
  function handleShowAll() {
    showingAll = true;
    renderRankingRows(allRankedBrands.length);
    rankingsActions.style.display = 'none';
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
